import { useState, useRef, useCallback, useEffect } from 'react';
import api from '../../api/axios';

/**
 * SurveyCard — Trait Importance Ranking Card
 *
 * Appears every 10 swipes in the Discover feed. Presents 4 randomly selected
 * trait categories for the user to rank by importance via drag-to-reorder.
 *
 * Design principles:
 *   - Clear hierarchy: large headline, concise instruction, numbered list
 *   - Minimal cognitive load: only 4 items, unambiguous ranking (1-4)
 *   - Touch-first: drag handle with haptic-style visual feedback
 *   - Progressive disclosure: appears naturally in the swipe flow
 *   - Non-blocking: can be dismissed/skipped if user prefers
 */

const GROUP_ICONS = {
  Lifestyle: '🌿',
  Background: '🎓',
  Physical: '💪',
  Personality: '💬',
};

const styles = {
  card: {
    position: 'absolute',
    inset: 0,
    borderRadius: 'var(--radius-lg)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    background: 'linear-gradient(168deg, #FFFFFF 0%, #FAFAFA 60%, #FFF5F6 100%)',
    border: '1px solid rgba(232, 87, 109, 0.3)',
    boxShadow: '0 0 30px rgba(0, 0, 0, 0.1), var(--shadow-lg)',
  },
  header: {
    padding: '28px 24px 16px',
    textAlign: 'center',
    flexShrink: 0,
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 12px',
    borderRadius: '12px',
    background: 'rgba(232, 87, 109, 0.15)',
    color: 'var(--accent)',
    fontSize: '11px',
    fontWeight: '600',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    marginBottom: '16px',
  },
  title: {
    fontSize: '24px',
    fontWeight: '800',
    color: '#111827',
    marginBottom: '8px',
    letterSpacing: '-0.3px',
  },
  subtitle: {
    fontSize: '14px',
    color: 'var(--text-muted)',
    lineHeight: 1.4,
  },
  listContainer: {
    flex: 1,
    padding: '8px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    minHeight: 0,
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px 16px',
    borderRadius: '12px',
    background: '#F3F4F6',
    border: '1px solid #E5E7EB',
    cursor: 'grab',
    transition: 'all 180ms ease',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    position: 'relative',
    overflow: 'hidden',
  },
  itemDragging: {
    background: 'rgba(232, 87, 109, 0.08)',
    border: '1px solid rgba(232, 87, 109, 0.4)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
    transform: 'scale(1.03)',
    zIndex: 10,
    cursor: 'grabbing',
  },
  itemDropTarget: {
    background: 'rgba(232, 87, 109, 0.05)',
    border: '1px dashed rgba(232, 87, 109, 0.3)',
  },
  rankNumber: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '13px',
    fontWeight: '700',
    flexShrink: 0,
  },
  rankColors: [
    { background: 'rgba(232, 87, 109, 0.12)', color: '#E8576D' },
    { background: 'rgba(232, 87, 109, 0.08)', color: 'rgba(232, 87, 109, 0.7)' },
    { background: '#F3F4F6', color: '#9CA3AF' },
    { background: '#F9FAFB', color: '#9CA3AF' },
  ],
  itemContent: {
    flex: 1,
    minWidth: 0,
  },
  itemLabel: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#111827',
  },
  itemGroup: {
    fontSize: '11px',
    color: 'var(--text-muted)',
    marginTop: '2px',
  },
  dragHandle: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    opacity: 0.4,
    flexShrink: 0,
    padding: '4px',
  },
  dragLine: {
    width: '16px',
    height: '2px',
    borderRadius: '1px',
    background: 'var(--text-muted)',
  },
  actions: {
    padding: '16px 20px 24px',
    display: 'flex',
    gap: '10px',
    flexShrink: 0,
  },
  submitBtn: {
    flex: 2,
    padding: '14px',
    borderRadius: '12px',
    border: 'none',
    background: 'var(--accent)',
    color: '#fff',
    fontSize: '15px',
    fontWeight: '700',
    cursor: 'pointer',
    transition: 'all 200ms',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
  skipBtn: {
    flex: 1,
    padding: '14px',
    borderRadius: '12px',
    border: '1px solid #E5E7EB',
    background: 'transparent',
    color: 'var(--text-muted)',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 200ms',
  },
  successOverlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(255, 255, 255, 0.97)',
    borderRadius: 'var(--radius-lg)',
    animation: 'fadeIn 300ms ease',
  },
  successIcon: {
    fontSize: '48px',
    marginBottom: '12px',
  },
  successText: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#111827',
    marginBottom: '6px',
  },
  successSub: {
    fontSize: '13px',
    color: 'var(--text-muted)',
  },
};

export default function SurveyCard({ traits, onComplete, onSkip, style }) {
  const [items, setItems] = useState(traits || []);
  const [draggingIndex, setDraggingIndex] = useState(null);
  const [dropTargetIndex, setDropTargetIndex] = useState(null);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const dragStartY = useRef(0);
  const itemRefs = useRef([]);

  useEffect(() => {
    if (traits) setItems(traits);
  }, [traits]);

  // ──── Touch/Mouse drag-to-reorder ────

  const handleDragStart = useCallback((index, e) => {
    e.preventDefault();
    e.stopPropagation();
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    dragStartY.current = clientY;
    setDraggingIndex(index);
  }, []);

  const handleDragMove = useCallback((e) => {
    if (draggingIndex === null) return;
    e.preventDefault();
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    // Determine which index the drag is currently over
    for (let i = 0; i < itemRefs.current.length; i++) {
      const el = itemRefs.current[i];
      if (!el) continue;
      const rect = el.getBoundingClientRect();
      if (clientY >= rect.top && clientY <= rect.bottom) {
        if (i !== draggingIndex) {
          setDropTargetIndex(i);
        }
        break;
      }
    }
  }, [draggingIndex]);

  const handleDragEnd = useCallback(() => {
    if (draggingIndex === null) return;

    if (dropTargetIndex !== null && dropTargetIndex !== draggingIndex) {
      // Reorder: remove from old position, insert at new position
      setItems((prev) => {
        const newItems = [...prev];
        const [moved] = newItems.splice(draggingIndex, 1);
        newItems.splice(dropTargetIndex, 0, moved);
        return newItems;
      });
    }

    setDraggingIndex(null);
    setDropTargetIndex(null);
  }, [draggingIndex, dropTargetIndex]);

  // Attach global listeners for drag
  useEffect(() => {
    if (draggingIndex === null) return;

    const onMove = (e) => handleDragMove(e);
    const onEnd = () => handleDragEnd();

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onEnd);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onEnd);

    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onEnd);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
    };
  }, [draggingIndex, handleDragMove, handleDragEnd]);

  // ──── Button tap reorder (accessibility alternative) ────

  const moveItem = (fromIndex, direction) => {
    const toIndex = fromIndex + direction;
    if (toIndex < 0 || toIndex >= items.length) return;
    setItems((prev) => {
      const newItems = [...prev];
      [newItems[fromIndex], newItems[toIndex]] = [newItems[toIndex], newItems[fromIndex]];
      return newItems;
    });
  };

  // ──── Submit ────

  const handleSubmit = async () => {
    if (saving) return;
    setSaving(true);

    const rankings = items.map((item, index) => ({
      key: item.key,
      rank: index + 1,
    }));

    try {
      await api.post('/discover/survey', { rankings });
      setSuccess(true);
      setTimeout(() => {
        onComplete?.();
      }, 1200);
    } catch (err) {
      console.error('Failed to save survey:', err);
      setSaving(false);
    }
  };

  if (!items.length) return null;

  return (
    <div style={{ ...styles.card, ...(style || {}) }}>
      {success ? (
        <div style={styles.successOverlay}>
          <div style={styles.successIcon}>✨</div>
          <div style={styles.successText}>Got it!</div>
          <div style={styles.successSub}>Your preferences are shaping better matches</div>
        </div>
      ) : (
        <>
          {/* Header */}
          <div style={styles.header}>
            <div style={styles.badge}>
              <span>📊</span>
              <span>Quick Survey</span>
            </div>
            <h2 style={styles.title}>Sort by importance</h2>
            <p style={styles.subtitle}>
              Most important at the top — drag to reorder
            </p>
          </div>

          {/* Sortable List */}
          <div style={styles.listContainer}>
            {items.map((item, index) => {
              const isDragging = draggingIndex === index;
              const isDropTarget = dropTargetIndex === index && draggingIndex !== null;
              const rankStyle = styles.rankColors[index] || styles.rankColors[3];

              return (
                <div
                  key={item.key}
                  ref={(el) => { itemRefs.current[index] = el; }}
                  style={{
                    ...styles.item,
                    ...(isDragging ? styles.itemDragging : {}),
                    ...(isDropTarget ? styles.itemDropTarget : {}),
                  }}
                  onMouseDown={(e) => handleDragStart(index, e)}
                  onTouchStart={(e) => handleDragStart(index, e)}
                >
                  {/* Rank number */}
                  <div style={{ ...styles.rankNumber, ...rankStyle }}>
                    {index + 1}
                  </div>

                  {/* Content */}
                  <div style={styles.itemContent}>
                    <div style={styles.itemLabel}>
                      {GROUP_ICONS[item.group] || '•'} {item.label}
                    </div>
                    <div style={styles.itemGroup}>{item.group}</div>
                  </div>

                  {/* Up/Down buttons (accessibility) */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    {index > 0 && (
                      <button
                        onClick={(e) => { e.stopPropagation(); moveItem(index, -1); }}
                        style={{
                          background: 'none', border: 'none', color: 'var(--text-muted)',
                          fontSize: '12px', cursor: 'pointer', padding: '2px 4px', lineHeight: 1,
                        }}
                        aria-label="Move up"
                      >
                        ▲
                      </button>
                    )}
                    {index < items.length - 1 && (
                      <button
                        onClick={(e) => { e.stopPropagation(); moveItem(index, 1); }}
                        style={{
                          background: 'none', border: 'none', color: 'var(--text-muted)',
                          fontSize: '12px', cursor: 'pointer', padding: '2px 4px', lineHeight: 1,
                        }}
                        aria-label="Move down"
                      >
                        ▼
                      </button>
                    )}
                  </div>

                  {/* Drag handle */}
                  <div style={styles.dragHandle}>
                    <div style={styles.dragLine} />
                    <div style={styles.dragLine} />
                    <div style={styles.dragLine} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Actions */}
          <div style={styles.actions}>
            <button
              style={styles.skipBtn}
              onClick={onSkip}
            >
              Skip
            </button>
            <button
              style={{
                ...styles.submitBtn,
                ...(saving ? { opacity: 0.7, cursor: 'default' } : {}),
              }}
              onClick={handleSubmit}
              disabled={saving}
            >
              {saving ? 'Saving...' : '✓ Confirm Ranking'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
